import React, { useEffect, useState } from "react";
import { Plus, FileText, Trash2, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState, Badge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function EducatorTests() {
  const [tests, setTests] = useState([]);
  const [managing, setManaging] = useState(null);
  const load = () => api.get("/tests/manage").then(({ data }) => setTests(data));
  useEffect(() => { load(); }, []);

  if (managing) return <TestQuestions test={managing} onBack={() => { setManaging(null); load(); }} />;

  return (
    <div data-testid="educator-tests">
      <PageHeader title="Test Series" subtitle="Create tests with objective & subjective questions" action={<NewTest onDone={load} />} />
      {tests.length === 0 ? <EmptyState icon={FileText} title="No tests created" /> : (
        <div className="space-y-3">
          {tests.map((t) => (
            <div key={t.id} className="bg-white border border-border rounded-lg p-4 flex items-center gap-4" data-testid={`etest-${t.id}`}>
              <div className="h-11 w-11 rounded-lg viriks-navy text-amber flex items-center justify-center"><FileText size={20} /></div>
              <div className="flex-1"><h3 className="font-semibold text-navy">{t.title}</h3><p className="text-sm text-muted-foreground">{t.question_count} questions • {t.duration} min • {t.test_type}</p></div>
              <Badge tone="green">{t.status}</Badge>
              <Button variant="outline" size="sm" onClick={() => setManaging(t)} data-testid={`manage-test-${t.id}`}>Questions <ChevronRight size={14} /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewTest({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", test_type: "objective", duration: "30", total_marks: "0", passing_marks: "0", negative_marking: "0", instructions: "Answer all questions carefully.", price: "0", proctoring: { camera: false, tab_switch: true, fullscreen: true, max_violations: 3 } });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.title) return toast.error("Enter title");
    await api.post("/tests", f); toast.success("Test created"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="viriks-navy text-white" data-testid="new-test-btn"><Plus size={16} className="mr-1" /> New Test</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>Create Test</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="test-title-inp" /></div>
          <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={f.test_type} onValueChange={(v) => set("test_type", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="objective">Objective</SelectItem><SelectItem value="subjective">Subjective</SelectItem><SelectItem value="mixed">Mixed</SelectItem></SelectContent></Select>
            </div>
            <div><Label>Duration (min)</Label><Input type="number" value={f.duration} onChange={(e) => set("duration", e.target.value)} /></div>
            <div><Label>Total Marks</Label><Input type="number" value={f.total_marks} onChange={(e) => set("total_marks", e.target.value)} /></div>
            <div><Label>Passing Marks</Label><Input type="number" value={f.passing_marks} onChange={(e) => set("passing_marks", e.target.value)} /></div>
            <div><Label>Negative Marking</Label><Input type="number" step="0.25" value={f.negative_marking} onChange={(e) => set("negative_marking", e.target.value)} /></div>
            <div><Label>Price (₹)</Label><Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} /></div>
          </div>
          <div className="border-t border-border pt-3">
            <p className="font-semibold text-sm mb-2">Proctoring Controls</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {["camera", "tab_switch", "fullscreen"].map((k) => (
                <label key={k} className="flex items-center gap-2 capitalize"><input type="checkbox" checked={f.proctoring[k]} onChange={(e) => set("proctoring", { ...f.proctoring, [k]: e.target.checked })} /> {k.replace("_", " ")}</label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button className="viriks-navy text-white" onClick={save} data-testid="save-test">Create Test</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TestQuestions({ test, onBack }) {
  const [questions, setQuestions] = useState([]);
  const load = () => api.get(`/tests/${test.id}`).then(({ data }) => setQuestions(data.questions || []));
  useEffect(() => { load(); }, [test.id]);
  return (
    <div data-testid="test-questions">
      <button onClick={onBack} className="text-sm text-muted-foreground mb-4">← Back to tests</button>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-navy">{test.title}</h1>
        <AddQuestion testId={test.id} onDone={load} />
      </div>
      {questions.length === 0 ? <EmptyState icon={FileText} title="No questions yet" /> : (
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={q.id} className="bg-white border border-border rounded-lg p-4">
              <div className="flex justify-between">
                <p className="font-medium text-navy">Q{i + 1}. {q.question}</p>
                <button onClick={async () => { await api.delete(`/questions/${q.id}`); load(); }} className="text-crimson"><Trash2 size={15} /></button>
              </div>
              <div className="mt-2 space-y-1 text-sm">
                {q.options.map((o) => <div key={o} className={q.correct.includes(o) ? "text-emerald-600 font-medium" : "text-muted-foreground"}>{q.correct.includes(o) ? "✓" : "•"} {o}</div>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddQuestion({ testId, onDone }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [type, setType] = useState("mcq");
  const [options, setOptions] = useState(["", "", "", ""]);
  const [correct, setCorrect] = useState([]);
  const [marks, setMarks] = useState("1");
  const save = async () => {
    const opts = options.filter(Boolean);
    if (!q || opts.length < 2 || correct.length === 0) return toast.error("Fill question, options & mark correct answer");
    await api.post(`/tests/${testId}/questions`, { question: q, type, options: opts, correct, marks });
    toast.success("Question added"); setOpen(false); setQ(""); setOptions(["", "", "", ""]); setCorrect([]); onDone();
  };
  const toggleCorrect = (o) => setCorrect((c) => type === "multiple" ? (c.includes(o) ? c.filter((x) => x !== o) : [...c, o]) : [o]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="viriks-navy text-white" data-testid="add-question-btn"><Plus size={16} className="mr-1" /> Add Question</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>Add Question</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Question</Label><Textarea value={q} onChange={(e) => setQ(e.target.value)} data-testid="q-text" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={type} onValueChange={setType}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="mcq">Single MCQ</SelectItem><SelectItem value="multiple">Multiple Correct</SelectItem><SelectItem value="truefalse">True/False</SelectItem></SelectContent></Select>
            </div>
            <div><Label>Marks</Label><Input type="number" value={marks} onChange={(e) => setMarks(e.target.value)} /></div>
          </div>
          <Label>Options (tick correct)</Label>
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="checkbox" checked={correct.includes(o) && o !== ""} onChange={() => o && toggleCorrect(o)} />
              <Input value={o} onChange={(e) => setOptions((p) => p.map((x, j) => j === i ? e.target.value : x))} placeholder={`Option ${i + 1}`} data-testid={`q-opt-${i}`} />
            </div>
          ))}
        </div>
        <DialogFooter><Button className="viriks-navy text-white" onClick={save} data-testid="save-question">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
